import { useEffect, useState } from "react";
import { Plus, Trash2, Clock, MapPin, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { DAYS } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const EMPTY = { class_id: "", day: "Senin", start_time: "16:00", end_time: "17:00", room: "" };

export default function Schedule() {
  const { user } = useAuth();
  const canManage = user?.role === "administrator";
  const [schedules, setSchedules] = useState([]);
  const [classes, setClasses] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/schedules").then((res) => setSchedules(res.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/classes").then((res) => setClasses(res.data)).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/schedules", form);
      toast.success("Jadwal ditambahkan");
      setOpen(false);
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(apiError(err, "Gagal menyimpan jadwal"));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Hapus jadwal ${s.class_name} hari ${s.day}?`)) return;
    try {
      await api.delete(`/schedules/${s.id}`);
      toast.success("Jadwal dihapus");
      load();
    } catch (err) {
      toast.error(apiError(err, "Gagal menghapus jadwal"));
    }
  };

  return (
    <div className="space-y-6" data-testid="schedule-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">Jadwal Les</h1>
          <p className="mt-1 text-sm text-muted-foreground">Jadwal mingguan seluruh kelas.</p>
        </div>
        {canManage && (
          <Button onClick={() => setOpen(true)} className="gap-2 bg-primary text-primary-foreground hover:bg-primary/90" data-testid="add-schedule-button">
            <Plus className="h-4 w-4" /> Tambah Jadwal
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
        {DAYS.map((day) => {
          const items = schedules.filter((s) => s.day === day);
          return (
            <div key={day} className="rounded-xl border border-border bg-white shadow-sm" data-testid={`schedule-day-${day.toLowerCase()}`}>
              <div className="border-b border-border px-4 py-3">
                <p className="font-heading text-sm font-bold uppercase tracking-wide text-primary">{day}</p>
              </div>
              <div className="space-y-3 p-4">
                {items.length === 0 ? (
                  <p className="py-3 text-center text-xs text-muted-foreground">Tidak ada jadwal</p>
                ) : items.map((s) => (
                  <div key={s.id} className="group rounded-lg border border-border p-3 transition-colors duration-200 hover:bg-slate-50" data-testid={`schedule-item-${s.id}`}>
                    <div className="flex items-start justify-between">
                      <p className="text-sm font-semibold">{s.class_name}</p>
                      {canManage && (
                        <Button variant="ghost" size="icon" className="h-7 w-7 opacity-0 transition-opacity duration-200 group-hover:opacity-100" onClick={() => remove(s)} data-testid={`delete-schedule-${s.id}`}>
                          <Trash2 className="h-3.5 w-3.5 text-destructive" />
                        </Button>
                      )}
                    </div>
                    <p className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground">
                      <Clock className="h-3.5 w-3.5" /> {s.start_time} - {s.end_time}
                    </p>
                    <p className="mt-0.5 text-xs text-muted-foreground">{s.teacher_name}</p>
                    {s.room && (
                      <p className="mt-0.5 flex items-center gap-1.5 text-xs text-muted-foreground">
                        <MapPin className="h-3.5 w-3.5" /> {s.room}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="schedule-form-dialog">
          <DialogHeader>
            <DialogTitle>Tambah Jadwal Les</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Kelas *</Label>
              <Select value={form.class_id} onValueChange={(v) => setForm({ ...form, class_id: v })}>
                <SelectTrigger data-testid="schedule-class-select"><SelectValue placeholder="Pilih kelas" /></SelectTrigger>
                <SelectContent>
                  {classes.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name} — {c.teacher_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Hari</Label>
              <Select value={form.day} onValueChange={(v) => setForm({ ...form, day: v })}>
                <SelectTrigger data-testid="schedule-day-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DAYS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Jam Mulai</Label>
                <Input type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} data-testid="schedule-start-input" />
              </div>
              <div className="space-y-2">
                <Label>Jam Selesai</Label>
                <Input type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} data-testid="schedule-end-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Ruangan</Label>
              <Input placeholder="cth: Studio 1" value={form.room} onChange={(e) => setForm({ ...form, room: e.target.value })} data-testid="schedule-room-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="schedule-cancel-button">Batal</Button>
            <Button onClick={save} disabled={saving || !form.class_id} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="schedule-save-button">
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Simpan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
